import { cn, Icon, type TableColumn, Tooltip } from "@kyan/ui";

import { getOptionOrPerpConstraints } from "#/config/orders";
import { tooltips } from "#/config/tooltips";
import { type PnlCardProps } from "#/types/pnlCard";
import { type Position } from "#/types/positions";
import {
  getInstrumentProperties,
  isOptionInstrument,
} from "#/utils/instrument.utils";
import { formatCurrency, formatValue } from "#/utils/valueFormatters";

import { SizeDisplay } from "../SizeDisplay/SizeDisplay";

export interface PositionColumnsProps {
  excludeColumnIds?: string[];
  openSharePnlCardsModal: (data: PnlCardProps) => void;
}

export const getCommonColumns = ({
  excludeColumnIds = [],
  openSharePnlCardsModal,
}: PositionColumnsProps): TableColumn<Position>[] => {
  const columns: TableColumn<Position>[] = [
    {
      id: "size",
      header: "Size",
      minWidth: "80px",
      cellClassName: ({ row }) =>
        cn({
          "text-success": row.size > 0,
          "text-error": row.size < 0,
        }),
      cell: ({ size, instrument }) => {
        const { underlying } = getInstrumentProperties(instrument);
        const isOption = isOptionInstrument(instrument);
        const { sizeDisplayPrecision } = getOptionOrPerpConstraints(instrument);

        return (
          <SizeDisplay
            value={size}
            precision={sizeDisplayPrecision}
            underlying={isOption ? undefined : underlying}
          />
        );
      },
    },
    {
      id: "avgPrice",
      header: "Avg Price",
      tooltip: tooltips.avgPrice,
      minWidth: "80px",
      cell: ({ avgPrice }) => formatCurrency(avgPrice),
    },
    {
      id: "markPrice",
      header: "Mark Price",
      tooltip: tooltips.markPrice,
      minWidth: "80px",
      cell: ({ markPrice }) => formatCurrency(markPrice),
    },
    {
      id: "pnl",
      header: "U. PnL",
      minWidth: "80px",
      cellClassName: ({ row }) =>
        cn({
          "text-success": row.pnl > 0,
          "text-error": row.pnl < 0,
        }),
      tooltip: tooltips.pnl,
      cell: ({
        pnl: uPnl,
        maturity,
        subPositions,
        instrument,
        size,
        avgPrice,
        markPrice,
      }) => {
        const isChild = Boolean(maturity) && subPositions === undefined;

        const isShort = size < 0;

        const pnl = isShort ? avgPrice - markPrice : markPrice - avgPrice;
        const calculatedPnlPct =
          Math.abs(pnl / (isShort ? markPrice : avgPrice)) * 100;

        return (
          <div className="flex items-center justify-between gap-2 sm:w-28 sm:pr-4">
            {formatCurrency(uPnl)}
            {isChild && (
              <Tooltip side="top" content="Share PnL Card">
                <Icon
                  size="xs"
                  name="share"
                  className="text-textSecondary"
                  onClick={() => {
                    openSharePnlCardsModal({
                      instrumentName: instrument,
                      side: isShort ? "short" : "long",
                      size: size.toString(),
                      entryPrice: avgPrice,
                      markPrice,
                      pnlPct: calculatedPnlPct,
                      pnlAbs: pnl,
                    });
                  }}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      id: "accruedFunding",
      header: "Acc. Funding",
      minWidth: "80px",
      tooltip: tooltips.accruedFunding,
      cellClassName: ({ row }) =>
        cn({
          "text-success": (row?.accruedFunding ?? 0) > 0,
          "text-error": (row?.accruedFunding ?? 0) < 0,
        }),
      cell: ({ accruedFunding, instrumentType }) =>
        isOptionInstrument(instrumentType)
          ? "-"
          : formatCurrency(accruedFunding),
    },
    {
      id: "delta",
      header: "∆ Delta",
      minWidth: "80px",
      tooltip: tooltips.delta,
      cell: ({ delta }) => formatValue(delta, { precision: 3 }),
    },
    {
      id: "gamma",
      header: "Γ Gamma",
      minWidth: "80px",
      tooltip: tooltips.gamma,
      cell: ({ gamma, instrumentType }) =>
        isOptionInstrument(instrumentType)
          ? formatValue(gamma, { precision: 5 })
          : "-",
    },
    {
      id: "vega",
      header: "V Vega",
      minWidth: "60px",
      tooltip: tooltips.vega,
      cell: ({ vega, instrumentType }) =>
        isOptionInstrument(instrumentType) ? formatValue(vega) : "-",
    },
    {
      id: "theta",
      header: "Θ Theta",
      minWidth: "80px",
      tooltip: tooltips.theta,
      cell: ({ theta, instrumentType }) =>
        isOptionInstrument(instrumentType) ? formatValue(theta) : "-",
    },
    {
      id: "rho",
      header: "ρ Rho",
      minWidth: "70px",
      tooltip: tooltips.rho,
      cell: ({ rho, instrumentType }) =>
        isOptionInstrument(instrumentType) ? formatValue(rho) : "-",
    },
  ];

  return columns.filter((column) => !excludeColumnIds?.includes(column.id));
};
